import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardDetailsAPI, leadAPI } from '../api';
import { toast } from 'sonner';
import { AlertTriangle, Clock, User as UserIcon, ExternalLink, UserPlus, X, Loader } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ReassignModal = ({ lead, onClose, onDone }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await dashboardDetailsAPI.reassignSuggestions(lead.id);
        setSuggestions(data.suggestions || []);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load suggestions'); }
      finally { setLoading(false); }
    })();
  }, [lead.id]);

  const reassign = async (userId) => {
    setAssigning(userId);
    try {
      await leadAPI.assign(lead.id, userId);
      toast.success('Reassigned successfully');
      onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reassign'); }
    finally { setAssigning(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-[#131A2B] rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reassign-modal">
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-outfit font-semibold">Reassign — {lead.customer_name}</h2>
            <p className="text-xs text-muted-foreground">Suggestions sorted by lightest workload first</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4">
          {loading ? <div className="flex justify-center py-8"><Loader className="h-6 w-6 animate-spin text-primary" /></div> :
           suggestions.length === 0 ? <p className="text-center text-sm text-muted-foreground py-8">No suggestions available.</p> :
           <div className="space-y-2">
             {suggestions.map((u, idx) => (
               <button key={u.id} onClick={() => reassign(u.id)} disabled={assigning === u.id}
                 data-testid={`reassign-suggestion-${u.id}`}
                 className={`w-full flex items-center justify-between p-3 border rounded-lg text-left transition-colors ${idx === 0 ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                 <div>
                   <p className="font-medium text-sm">{u.name}
                     {idx === 0 && <span className="ml-2 text-xs bg-primary text-white px-1.5 py-0.5 rounded">Recommended</span>}
                   </p>
                   <p className="text-xs text-muted-foreground capitalize">{u.role.replace('_', ' ')}</p>
                 </div>
                 <div className="text-right">
                   <p className="text-xs text-muted-foreground">Active leads</p>
                   <p className="font-semibold">{u.active_leads}</p>
                 </div>
               </button>
             ))}
           </div>}
        </div>
      </div>
    </div>
  );
};

export const DelayedProjectsWidget = ({ compact = false, thresholdDays = 7 }) => {
  const { user } = useAuth();
  const canReassign = user?.role === 'super_admin' || user?.role === 'manager';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [reassigning, setReassigning] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await dashboardDetailsAPI.delayedProjects(thresholdDays);
      setData(data);
      if (data.services?.length && !activeTab) setActiveTab(data.services[0].service_id || '_unassigned');
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholdDays]);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="clay-card p-5 flex items-center justify-center" data-testid="delayed-widget">
      <Loader className="h-5 w-5 animate-spin text-primary" />
    </div>
  );
  if (!data || data.total === 0) return (
    <div className="clay-card p-5 border-l-4 border-emerald-500" data-testid="delayed-widget">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <p className="font-medium">No delayed projects</p>
          <p className="text-xs text-muted-foreground">All projects are on track (threshold {thresholdDays} days)</p>
        </div>
      </div>
    </div>
  );

  const current = data.services.find(s => (s.service_id || '_unassigned') === activeTab) || data.services[0];
  const visibleProjects = compact ? current.projects.slice(0, 3) : current.projects;

  return (
    <div className="clay-card p-5 border-l-4 border-amber-500" data-testid="delayed-widget">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="font-outfit font-semibold text-lg">Delayed Projects</p>
            <p className="text-xs text-muted-foreground">{data.total} project{data.total !== 1 ? 's' : ''} stuck &gt;{thresholdDays} days or past start date</p>
          </div>
        </div>
        {compact && (
          <button onClick={() => navigate('/dashboard/delayed')} data-testid="delayed-widget-view-all"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1">
            View all <ExternalLink className="h-3 w-3" />
          </button>
        )}
      </div>

      <div className="flex gap-1 mb-3 overflow-x-auto" data-testid="delayed-widget-tabs">
        {data.services.map(s => {
          const key = s.service_id || '_unassigned';
          const active = activeTab === key;
          return (
            <button key={key} onClick={() => setActiveTab(key)}
              data-testid={`delayed-tab-${s.service_name}`}
              className={`px-3 py-1.5 rounded text-xs whitespace-nowrap ${active ? 'text-white' : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300'}`}
              style={active ? { backgroundColor: s.color } : {}}>
              {s.service_name} ({s.projects.length})
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {visibleProjects.map(p => (
          <div key={p.id} data-testid={`delayed-project-${p.id}`}
            className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/40">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm truncate">{p.customer_name}</p>
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded whitespace-nowrap">
                  <Clock className="inline h-3 w-3 mr-0.5" />{p.days_delayed}d
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {p.assigned_to_name || 'Unassigned'} · {p.workflow_stage_name || p.status?.replace(/_/g, ' ') || '—'}
              </p>
              {p.delay_reasons?.length > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 truncate">
                  {p.delay_reasons.join(' · ')}
                </p>
              )}
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => navigate(`/leads/${p.id}`)} data-testid={`delayed-open-${p.id}`}
                className="text-xs px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-100 dark:hover:bg-slate-800 inline-flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> Open
              </button>
              {canReassign && (
                <button onClick={() => setReassigning(p)} data-testid={`delayed-reassign-${p.id}`}
                  className="text-xs px-2.5 py-1.5 bg-primary text-white rounded hover:bg-primary/90 inline-flex items-center gap-1">
                  <UserPlus className="h-3 w-3" /> Reassign
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {compact && current.projects.length > 3 && (
        <button onClick={() => navigate('/dashboard/delayed')}
          className="mt-3 w-full text-xs text-primary hover:underline">
          +{current.projects.length - 3} more in {current.service_name}
        </button>
      )}

      {reassigning && (
        <ReassignModal lead={reassigning} onClose={() => setReassigning(null)} onDone={load} />
      )}
    </div>
  );
};

export const DelayedProjectsPage = () => {
  const [threshold, setThreshold] = useState(7);
  return (
    <div className="space-y-6" data-testid="delayed-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-outfit font-semibold">Delayed Projects</h1>
          <p className="text-muted-foreground">Projects stuck in workflow or past their expected start date</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-muted-foreground text-xs">Threshold</label>
          <select value={threshold} onChange={(e) => setThreshold(parseInt(e.target.value))}
            data-testid="delayed-threshold-select"
            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
            <option value={3}>3 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
      </div>
      <DelayedProjectsWidget compact={false} thresholdDays={threshold} />
    </div>
  );
};

export default DelayedProjectsPage;
